<template>
	<BaseModal
		:model-value="props.modelValue"
		@update:model-value="$emit('update:modelValue', $event)"
		:title="props.package.sent ? 'Revisar Respuesta SEP' : 'Enviar a la SEP'"
		:test-mode="props.testMode"
		message=""
		:size="props.package.sent ? 'large' : 'default'"
		class="max-w-4xl mx-auto">
		<template #body>
			<!-- Loading State -->
			<div v-if="loading" class="flex flex-col items-center justify-center py-16 px-8">
				<div class="loader mb-8"></div>
				<p class="text-xl text-gray-700 font-medium text-center leading-relaxed">{{ loadingMessage }}</p>
				<p class="text-sm text-gray-500 mt-2 text-center">Por favor espera mientras procesamos tu solicitud...</p>
			</div>

			<!-- Main Content -->
			<div v-else class="space-y-6">
				<!-- Información del Paquete -->
				<section class="package-section">
					<h3 class="section-title">
						<VaIcon name="description" class="title-icon" />
						Información del Paquete
					</h3>
					<div class="package-grid">
						<!-- Nombre del Paquete -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="badge" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Nombre</h5>
									<p :class="['package-value', props.testMode ? 'text-test' : 'text-primary']">{{ props.package.label }}</p>
								</div>
							</div>
						</div>

						<!-- Fecha -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="event" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Fecha</h5>
									<p class="package-value">{{ formatDate(props.package.id) }}</p>
								</div>
							</div>
						</div>

						<!-- Número de Títulos -->
						<div class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="collections_bookmark" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Número de Títulos</h5>
									<p class="package-value">{{ props.package.numDegrees }}</p>
								</div>
							</div>
						</div>

						<!-- Estado -->
						<div v-if="props.package.sent" class="package-card">
							<div class="package-header">
								<div class="package-avatar">
									<VaIcon name="check_circle" class="package-icon" />
								</div>
								<div class="package-info">
									<h5 class="package-name">Estado</h5>
									<p :class="['package-value', getStatusClass]">{{ getStatusText(props.package) }}</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Responsables -->
				<section class="signers-section">
					<h4 class="signers-title">
						<VaIcon name="groups" class="title-icon" />
						Responsables
					</h4>
					<div class="signers-grid">
						<div v-for="(responsable, index) in props.package.responsables" :key="index" class="signer-card">
							<div class="signer-header">
								<div class="signer-avatar">
									<VaIcon name="person" class="signer-icon" />
								</div>
								<div class="signer-info">
									<h5 class="signer-name">{{ responsable.cargo }}</h5>
									<p class="signer-role">{{ responsable.name.trim() }}</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Resultado SEP -->
				<section v-if="props.package.procesedBySep" class="result-section">
					<h4 class="section-title">
						<VaIcon name="task_alt" class="title-icon" />
						Resultado SEP
					</h4>
					<div class="result-table-container">
						<table class="result-table">
							<thead>
								<tr class="table-header">
									<th>Archivo</th>
									<th>Folio Control</th>
									<th>Estado</th>
									<th>Descripción</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(result, index) in props.package.sepResult" :key="index" class="table-row">
									<td class="table-cell">{{ result.ARCHIVO }}</td>
									<td class="table-cell font-mono">{{ result['FOLIO CONTROL'] }}</td>
									<td class="table-cell">
										<span :class="getStatusBadgeClass(result.ESTATUS)">
											{{ getStatusDegreeText(result.ESTATUS) }}
										</span>
									</td>
									<td class="table-cell">{{ result.DESCRIPCION }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>
			</div>
		</template>

		<template #footer>
			<div class="modal-actions">
				<button class="btn btn-secondary" @click="closeModal" :disabled="loading">
					<VaIcon name="close" class="btn-icon" />
					Cerrar
				</button>

				<button v-if="props.package.procesedBySep === false && props.package.sent" class="btn btn-primary" @click="handleAction" :disabled="loading">
					<VaIcon v-if="!loading" name="refresh" class="btn-icon" />
					<VaIcon v-else name="refresh" class="btn-icon animate-spin" />
					{{ loading ? 'Consultando...' : 'Revisar Respuesta' }}
				</button>

				<button v-else-if="!props.package.sent" class="btn btn-primary" @click="handleAction" :disabled="loading">
					<VaIcon v-if="!loading" name="send" class="btn-icon" />
					<VaIcon v-else name="refresh" class="btn-icon animate-spin" />
					{{ loading ? 'Enviando...' : 'Enviar a la SEP' }}
				</button>
			</div>
		</template>
	</BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, onMounted } from 'vue'
import BaseModal from './BaseModal.vue'
import { degreeService } from '@/services/degreeService'
import { useStore } from '@/store'
import { IDegreePackage } from '@shared/types'
import { formatDate } from '@/utils/dateFormatter'
import { IExtendedDegreePackage } from '@shared/types/extensions'
import { ref as dbRef, onValue, off } from 'firebase/database'
import { database } from '@/firebase/config'
import { VaIcon } from 'vuestic-ui'

const props = withDefaults(
	defineProps<{
		modelValue: boolean
		package: IExtendedDegreePackage
		institution: string
		testMode: boolean
	}>(),
	{
		modelValue: false,
		package: () => ({}) as IExtendedDegreePackage,
		institution: '',
		testMode: false
	}
)

const emit = defineEmits(['update:modelValue', 'update:package'])

const store = useStore()
const loading = ref(false)
const loadingMessage = ref('')
let packageListener: (() => void) | null = null

const setupPackageListener = () => {
	const path = `/institutions/${props.institution}${props.testMode ? '/testMode' : ''}/degrees/packages/${props.package.id}`
	const packageRef = dbRef(database, path)

	// Limpiar listener anterior si existe
	if (packageListener) {
		packageListener()
	}

	// Configurar nuevo listener
	packageListener = () => {
		off(packageRef)
	}

	onValue(packageRef, snapshot => {
		const data = snapshot.val()
		if (data) {
			emit('update:package', { ...props.package, ...data })
		}
	})
}

// Configurar listener cuando el modal se abre

onMounted(() => {
	setupPackageListener()
})

// Limpiar listener cuando el componente se desmonta
onUnmounted(() => {
	if (packageListener) {
		packageListener()
		packageListener = null
	}
})

const closeModal = () => {
	emit('update:modelValue', false)
}

const handleAction = async () => {
	try {
		loading.value = true
		store.dispatch('loader/setLoading', {
			isLoading: true,
			message: props.package.sent ? 'Consultando estado en la SEP...' : 'Enviando paquete a la SEP...'
		})

		if (props.package.sent) {
			// Si el paquete ya fue enviado, consultar estado
			const response = await degreeService.getResponseDegreeSEP({
				institution: props.institution,
				package: props.package.id,
				testMode: props.testMode
			})

			if (response) {
				store.dispatch('notification/showSuccess', 'Estado del paquete actualizado exitosamente')
				emit('update:package', { ...props.package, ...response })
			}
		} else {
			// Si el paquete no ha sido enviado, validar y enviar
			if (!props.package.sign) {
				store.dispatch('notification/showError', 'El paquete debe estar firmado antes de enviarlo a la SEP')
				return
			}

			if (props.package.sent) {
				store.dispatch('notification/showError', 'Este paquete ya ha sido enviado a la SEP')
				return
			}

			const response = await degreeService.sendDegreesPackageToSEP({
				institution: props.institution,
				package: props.package.id,
				testMode: props.testMode
			})

			if (response) {
				store.dispatch('notification/showSuccess', 'Paquete enviado exitosamente a la SEP')
				emit('update:package', { ...props.package, ...response })
			}
		}
	} catch (error: any) {
		console.error('Error en operación SEP:', error)
		const errorMessage = error?.message || 'Error al procesar la operación con la SEP'
		store.dispatch('notification/showError', errorMessage)
	} finally {
		loading.value = false
		store.dispatch('loader/setLoading', { isLoading: false })
	}
}

const getStatusClass = computed(() => {
	if (props.package.procesedBySep) {
		return 'text-success'
	}
	return 'text-warning'
})

const getStatusText = (pkg: IDegreePackage) => {
	if (pkg.procesedBySep) {
		return 'Procesado por la SEP'
	}
	return 'Pendiente de procesamiento'
}

const getStatusDegreeText = (status: number) => {
	switch (status) {
		case 1:
			return 'Exitoso'
		case 2:
			return 'Error'
		case 0:
			return 'Pendiente'
		default:
			return 'Desconocido'
	}
}

const getStatusBadgeClass = (status: number) => {
	switch (status) {
		case 1:
			return 'px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800'
		case 2:
			return 'px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800'
		case 0:
			return 'px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800'
		default:
			return 'px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800'
	}
}
</script>

<style scoped>
/* Loader mejorado */
.loader {
	border: 4px solid #f3f4f6;
	border-radius: 50%;
	border-top: 4px solid #007bff;
	width: 60px;
	height: 60px;
	animation: spin 1s linear infinite;
	box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Estados de texto */
.text-primary {
	color: #007bff;
}
.text-secondary {
	color: #6c757d;
}
.text-success {
	color: #28a745;
}
.text-warning {
	color: #ffc107;
}
.text-test {
	color: #ea580c;
	font-weight: bold;
}

/* Fondos */
.bg-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}
.bg-primary-dark {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}
.bg-test {
	background: linear-gradient(135deg, #ea580c 0%, #dc2626 100%);
}

/* Secciones principales */
.package-section,
.signers-section,
.result-section {
	margin-bottom: 2rem;
}

.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.title-icon {
	color: #007bff;
	font-size: 1.25rem;
}

/* Grid de paquetes */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 1rem;
}

.package-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.package-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.package-header {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.package-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.package-icon {
	color: white;
	font-size: 1.25rem;
}

.package-info {
	flex: 1;
	min-width: 0;
}

.package-name {
	font-size: 0.875rem;
	font-weight: 500;
	color: #6c757d;
	margin: 0 0 0.25rem 0;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

.package-value {
	font-size: 1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0;
	line-height: 1.3;
}

/* Sección de firmantes */
.signers-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.signers-grid {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.signer-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.signer-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.signer-header {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.signer-avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.25rem;
	flex-shrink: 0;
}

.signer-icon {
	color: white;
	font-size: 1.25rem;
}

.signer-info {
	flex: 1;
	min-width: 0;
}

.signer-name {
	font-size: 1.1rem;
	font-weight: 600;
	color: #1a2a3a;
	margin: 0 0 0.25rem 0;
	line-height: 1.3;
}

.signer-role {
	font-size: 0.9rem;
	color: #6c757d;
	margin: 0;
	font-weight: 500;
}

/* Sección de resultados */
.result-table-container {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.result-table {
	width: 100%;
	border-collapse: collapse;
}

.table-header {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.table-header th {
	padding: 1rem;
	text-align: left;
	font-weight: 600;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.025em;
}

.table-row {
	border-bottom: 1px solid #e9ecef;
	transition: background-color 0.2s ease;
}

.table-row:hover {
	background-color: #f8f9fa;
}

.table-cell {
	padding: 1rem;
	font-size: 0.875rem;
	color: #1a2a3a;
}

/* Badges de estado */
.px-2.py-1.text-xs.font-medium.rounded-full.bg-green-100.text-green-800 {
	background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
	border: 1px solid #86efac;
	box-shadow: 0 2px 4px rgba(34, 197, 94, 0.15);
	font-weight: 600;
	letter-spacing: 0.025em;
}

.px-2.py-1.text-xs.font-medium.rounded-full.bg-red-100.text-red-800 {
	background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
	border: 1px solid #fca5a5;
	box-shadow: 0 2px 4px rgba(239, 68, 68, 0.15);
	font-weight: 600;
	letter-spacing: 0.025em;
}

.px-2.py-1.text-xs.font-medium.rounded-full.bg-yellow-100.text-yellow-800 {
	background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
	border: 1px solid #fcd34d;
	box-shadow: 0 2px 4px rgba(245, 158, 11, 0.15);
	font-weight: 600;
	letter-spacing: 0.025em;
}

.px-2.py-1.text-xs.font-medium.rounded-full.bg-gray-100.text-gray-800 {
	background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
	border: 1px solid #d1d5db;
	box-shadow: 0 2px 4px rgba(107, 114, 128, 0.15);
	font-weight: 600;
	letter-spacing: 0.025em;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
	transform: none;
}

.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}

.btn-secondary {
	background: linear-gradient(135deg, #6c757d 0%, #545b62 100%);
	color: white;
}

.btn-secondary:hover {
	background: linear-gradient(135deg, #545b62 0%, #3d4449 100%);
}

.btn-icon {
	width: 1rem;
	height: 1rem;
	margin-right: 0.5rem;
}

/* Acciones del modal */
.modal-actions {
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
	padding: 1rem 0;
}

/* Responsive design */
@media (max-width: 768px) {
	.package-grid {
		grid-template-columns: 1fr;
	}

	.signer-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.75rem;
	}

	.signer-card,
	.package-card {
		padding: 1rem;
	}

	.modal-actions {
		flex-direction: column;
	}

	.btn {
		width: 100%;
		justify-content: center;
	}

	.table-header th,
	.table-cell {
		padding: 0.75rem 0.5rem;
		font-size: 0.75rem;
	}
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.package-card,
.signer-card {
	animation: fadeIn 0.5s ease-in-out;
}

.result-table-container::-webkit-scrollbar {
	height: 8px;
}

.result-table-container::-webkit-scrollbar-track {
	background: #f1f5f9;
	border-radius: 4px;
}

.result-table-container::-webkit-scrollbar-thumb {
	background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
	border-radius: 4px;
}

.result-table-container::-webkit-scrollbar-thumb:hover {
	background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
}
</style>
